/**
 * ZIP Code Boundaries Service
 *
 * Service for fetching ZIP code boundary geometries and unioning them into a single polygon
 */

import * as turf from '@turf/turf';
import type { Feature, Polygon, MultiPolygon } from 'geojson';
import type { NormalizedGeometry } from '../utils/zoneGeometry';

export interface ZipCodeBoundaryResponse {
  type: 'FeatureCollection';
  features: Array<{
    type: 'Feature';
    properties: {
      ZCTA5: string; // ZIP code
      [key: string]: unknown;
    };
    geometry: Polygon | MultiPolygon;
  }>;
  // Requested ZIPs with no Census ZCTA polygon (PO-box / unique ZIPs). Normal, not an error.
  missing_zip_codes?: string[];
}

/** [minLng, minLat, maxLng, maxLat] in WGS84 */
export type BBox = [number, number, number, number];

export interface ZipBoundaryOptions {
  // Bounding box of the shape the ZIPs were picked from. get-zips uses it as a
  // spatial index hint: TIGERweb answers a ZIP-list query in <1 s with it and
  // ~14 s without. It can't drop a ZIP — every ZIP was chosen by intersecting
  // that shape, so it intersects the shape's box too.
  bbox?: BBox | undefined;
}

/**
 * Padded bounding box of a Polygon/MultiPolygon, or undefined for anything else.
 * Takes `unknown` so callers can pass `zone.metadata.geometry` straight through.
 * The padding only guards float edges.
 */
export function bboxForGeometry(geometry: unknown, padDeg = 0.01): BBox | undefined {
  const g = geometry as Partial<NormalizedGeometry> | null | undefined;
  if (!g || (g.type !== 'Polygon' && g.type !== 'MultiPolygon') || !Array.isArray(g.coordinates)) {
    return undefined;
  }
  const [minLng, minLat, maxLng, maxLat] = turf.bbox(g as Polygon | MultiPolygon);
  if (![minLng, minLat, maxLng, maxLat].every(Number.isFinite)) {
    return undefined;
  }
  return [minLng - padDeg, minLat - padDeg, maxLng + padDeg, maxLat + padDeg];
}

/**
 * Fetch ZIP code boundaries from Census TIGERweb API
 * Uses the existing get-zips Edge Function, but requests geometries instead of just ZIP codes.
 *
 * Exported so callers that want the **individual** ZIP boundaries (not the
 * unioned zone outline produced by `createZoneBoundaryFromZipCodes`) can
 * consume the raw FeatureCollection — e.g. the Presentation-mode renderer
 * that draws ZIP subdivisions inside each zone.
 *
 * Throws on any non-OK response — get-zips returns 502 rather than a partial result.
 */
export async function fetchZipCodeBoundaries(
  zipCodes: string[],
  options: ZipBoundaryOptions = {}
): Promise<Feature<Polygon | MultiPolygon>[]> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const projectRef = supabaseUrl?.match(/https?:\/\/([^.]+)\.supabase\.co/)?.[1];

  if (!projectRef) {
    throw new Error('Invalid Supabase URL configuration');
  }

  // Get session for authenticated request
  const { supabase } = await import('@vehix/shared/admin/portal-bridge');
  const {
    data: { session }
  } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('Authentication required');
  }

  const edgeFunctionUrl = `${supabaseUrl}/functions/v1/get-zips`;

  const response = await fetch(edgeFunctionUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY || ''
    },
    body: JSON.stringify({
      zip_codes: zipCodes,
      return_geometry: true,
      ...(options.bbox && { bbox: options.bbox })
    })
  });

  if (!response.ok) {
    // get-zips answers 400/502 with { error }; a gateway 504 has a plain-text body
    const errorText = await response.text();
    let detail = errorText;
    try {
      detail = (JSON.parse(errorText) as { error?: string }).error || errorText;
    } catch {
      // not JSON — keep the raw text
    }
    throw new Error(`Failed to fetch ZIP boundaries (HTTP ${response.status}): ${detail}`);
  }

  const data: ZipCodeBoundaryResponse = await response.json();

  return data.features.map(feature => ({
    type: 'Feature' as const,
    properties: feature.properties,
    geometry: feature.geometry
  }));
}

/**
 * Union multiple ZIP code boundaries into a single polygon
 * Uses Turf.js to merge all ZIP code geometries
 *
 * Returns null only when there is nothing to build from (no ZIPs, or none of them
 * has a Census polygon). Fetch failures throw so the caller can tell the user —
 * swallowing them here is how zones silently kept their hand-drawn shape when
 * get-zips started timing out (2026-11).
 */
export async function createZoneBoundaryFromZipCodes(
  zipCodes: string[],
  options: ZipBoundaryOptions = {}
): Promise<NormalizedGeometry | null> {
  if (!zipCodes || zipCodes.length === 0) {
    return null;
  }

  // Fetch boundaries for all ZIP codes
  const boundaries = await fetchZipCodeBoundaries(zipCodes, options);

  if (boundaries.length === 0) {
    return null;
  }

  // Remove any duplicate boundaries (same ZIP might be returned multiple times)
  const uniqueBoundaries = boundaries.reduce((acc: typeof boundaries, boundary) => {
    // Try different property names that Census API might use
    const zipCode =
      boundary.properties?.ZCTA5 ||
      boundary.properties?.ZCTA5CE10 ||
      boundary.properties?.GEOID ||
      boundary.properties?.ZCTA;

    const exists = acc.some(b => {
      const existingZip =
        b.properties?.ZCTA5 || b.properties?.ZCTA5CE10 || b.properties?.GEOID || b.properties?.ZCTA;
      return existingZip === zipCode;
    });

    if (!exists && zipCode) {
      acc.push(boundary);
    }
    return acc;
  }, []);

  // Union all boundaries into a single feature
  // If only one boundary, return it directly
  if (uniqueBoundaries.length === 1) {
    const singleBoundary = uniqueBoundaries[0];
    const geometry = singleBoundary.geometry;

    if (geometry.type === 'Polygon') {
      return {
        type: 'Polygon',
        coordinates: geometry.coordinates as number[][][]
      };
    } else if (geometry.type === 'MultiPolygon') {
      return {
        type: 'MultiPolygon',
        coordinates: geometry.coordinates as number[][][][]
      };
    }
    return null;
  }

  // For multiple boundaries, try different union strategies

  // Strategy 1: Try to create a MultiPolygon from all boundaries if union fails
  const allPolygons: Array<Polygon | MultiPolygon> = [];

  // Collect all polygons
  for (const boundary of uniqueBoundaries) {
    if (boundary.geometry) {
      if (boundary.geometry.type === 'Polygon') {
        allPolygons.push(boundary.geometry);
      } else if (boundary.geometry.type === 'MultiPolygon') {
        // Flatten MultiPolygon into individual Polygons
        for (const coords of boundary.geometry.coordinates) {
          allPolygons.push({
            type: 'Polygon',
            coordinates: coords
          });
        }
      }
    }
  }

  // Try different strategies
  let result: NormalizedGeometry | null = null;

  // Strategy 1: Create MultiPolygon from all ZIP code boundaries
  const multiPolygonCoordinates: number[][][][] = [];
  for (const boundary of uniqueBoundaries) {
    const geometry = boundary.geometry;
    if (geometry.type === 'Polygon') {
      multiPolygonCoordinates.push(geometry.coordinates);
    } else if (geometry.type === 'MultiPolygon') {
      // Add each polygon from the MultiPolygon
      multiPolygonCoordinates.push(...geometry.coordinates);
    }
  }

  if (multiPolygonCoordinates.length > 0) {
    result = {
      type: 'MultiPolygon',
      coordinates: multiPolygonCoordinates
    };
  }

  // Strategy 2: Create simplified boundary as fallback only if MultiPolygon failed
  const shouldUseFallback = !result;

  if (shouldUseFallback && allPolygons.length > 0) {
    try {
      // Create a FeatureCollection from all polygons
      const featureCollection: any = {
        type: 'FeatureCollection',
        features: allPolygons.map(polygon => ({
          type: 'Feature',
          geometry: polygon,
          properties: {}
        }))
      };

      // Try different approaches for a clean unified boundary
      let cleanResult: any = null;

      // Approach 1: Try buffering and unioning all polygons together
      try {
        // Create a union of all features using a more robust approach
        let combinedFeature = featureCollection.features[0];

        for (let i = 1; i < featureCollection.features.length && combinedFeature; i++) {
          try {
            // Add a tiny buffer to help with union operations
            const buffered1 = turf.buffer(combinedFeature, 0.0001, { units: 'kilometers' });
            const buffered2 = turf.buffer(featureCollection.features[i], 0.0001, {
              units: 'kilometers'
            });

            const unionAttempt = turf.union(buffered1, buffered2);
            if (unionAttempt?.geometry) {
              combinedFeature = unionAttempt;
            }
          } catch {
            // Continue with what we have
          }
        }

        if (combinedFeature?.geometry) {
          // Remove the tiny buffer to get back to original size
          try {
            const unbuffered = turf.buffer(combinedFeature, -0.0001, { units: 'kilometers' });
            cleanResult = unbuffered?.geometry ? unbuffered : combinedFeature;
          } catch {
            cleanResult = combinedFeature;
          }
        }
      } catch {
        // fall through to the convex hull
      }

      // Approach 2: If buffering failed, try convex hull as last resort
      if (!cleanResult) {
        try {
          // Get all coordinates from all polygons
          const allCoords: number[][] = [];
          featureCollection.features.forEach((feature: any) => {
            if (feature.geometry.type === 'Polygon') {
              // Add coordinates from outer ring only
              feature.geometry.coordinates[0].forEach((coord: number[]) => {
                allCoords.push(coord);
              });
            }
          });

          if (allCoords.length > 0) {
            const points = turf.featureCollection(allCoords.map(coord => turf.point(coord)));
            const hull = turf.convex(points);
            if (hull?.geometry) {
              cleanResult = hull;
            }
          }
        } catch {
          // no hull either — result stays null
        }
      }

      // Use the clean result if we got one
      if (cleanResult?.geometry) {
        result = cleanResult.geometry as NormalizedGeometry;
      }
    } catch {
      // every fallback failed — result stays null
    }
  }

  // MultiPolygon is kept as-is — it preserves the exact ZIP code boundaries
  return result;
}
